import { MinistryStatus } from '@core/api/generated/api-client';
import { Person } from '../models/person.model';
import { PersonEvent } from '../models/person-event.model';

export interface PersonMinistryActivityLine {
  activities: string[];
  units: { id?: string; name: string }[];
  ministryStatus?: MinistryStatus;
}

export function buildPersonMinistryActivityLines(person: Person, events: PersonEvent[]): PersonMinistryActivityLine[] {
  const ministry = person.ministry?.status === 'Terminated' ? undefined : person.ministry;
  const standaloneRoles = events
    .filter(event => (event.eventKind === 'officiant' || event.eventKind === 'appointment' || (event.eventKind === 'assignment' && event.requiresActiveMinistry)) && isCurrentEvent(event))
    .map(event => ({ name: event.eventTypeName, unitId: event.organizationUnitId, unitName: event.organizationUnitName }));
  const ministerialEventRoles = events.flatMap(event => (event.generatedActivityChanges ?? event.ministerialAreas ?? [])
    .filter(area =>
      (area.kind === 'Appointment' || area.kind === 'Assignment') &&
      area.changeType === 'Added' &&
      isCurrentPeriod(area.validFrom, area.validTo))
    .map(area => ({
      name: area.referenceName,
      unitId: area.organizationUnitId,
      unitName: area.organizationUnitName,
    })));
  const roles = [...standaloneRoles, ...ministerialEventRoles];
  const areas = new Map<string, { id?: string; name?: string; includesMinistry: boolean }>();
  const ministryAreas = ministry?.activities.filter(activity => !!activity.unitName) ?? [];

  for (const area of ministryAreas.length ? ministryAreas : ministry ? [ministry] : []) {
    const key = area.unitId || area.unitName || 'without-unit';
    areas.set(key, { id: area.unitId, name: area.unitName, includesMinistry: true });
  }

  for (const role of roles) {
    const key = role.unitId || role.unitName || 'without-unit';
    const area = areas.get(key);
    areas.set(key, {
      id: area?.id ?? role.unitId,
      name: area?.name ?? role.unitName,
      includesMinistry: area?.includesMinistry ?? false,
    });
  }

  const areaLines = [...areas.entries()].map(([key, area]) => {
    const matchingRoles = roles.filter(role => (role.unitId || role.unitName || 'without-unit') === key);
    const activities = [...new Set([
      ...(ministry && area.includesMinistry ? [ministry.ministryName] : []),
      ...matchingRoles.map(role => role.name),
    ])];

    return {
      activities,
      units: area.name ? [{ id: area.id, name: area.name }] : [],
      ministryStatus: ministry && area.includesMinistry ? ministry.status as MinistryStatus : undefined,
    };
  }).filter(line => line.activities.length > 0);

  const groupedLines = new Map<string, PersonMinistryActivityLine>();
  for (const line of areaLines) {
    const key = line.activities.join('\u0000');
    const existing = groupedLines.get(key);
    if (!existing) {
      groupedLines.set(key, line);
      continue;
    }

    const knownUnits = new Set(existing.units.map(unit => unit.id || unit.name));
    existing.units.push(...line.units.filter(unit => !knownUnits.has(unit.id || unit.name)));
    existing.ministryStatus ??= line.ministryStatus;
  }

  return [...groupedLines.values()];
}

function isCurrentEvent(event: PersonEvent): boolean {
  return isCurrentPeriod(event.eventDate, event.endDate);
}

function isCurrentPeriod(startDate?: string, endDate?: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (startDate) {
    const [year, month, day] = startDate.split('-').map(Number);
    if (new Date(year, month - 1, day) > today) {
      return false;
    }
  }

  if (!endDate) {
    return true;
  }

  const [year, month, day] = endDate.split('-').map(Number);
  return new Date(year, month - 1, day) >= today;
}
